// Сведения об организации, проводившей обследование
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  CFormLabel,
  CFormInput,
  CFormTextarea,
  CForm,
  CContainer,
  CRow,
  CCol,
  CFormCheck,
  CButton,
  CFormSelect
} from '@coreui/react';
import PropTypes from 'prop-types';

const OrganizationInfoTab = forwardRef(({ initialData, onSave }, ref) => {
  const [organization, setOrganization] = useState('ООО "Союзстальконструкция"');
  const [examinationDate, setExaminationDate] = useState('');
  const [takeDateFromPhotos, setTakeDateFromPhotos] = useState(false);
  const [nextExaminationDate, setNextExaminationDate] = useState('');
  const [reason, setReason] = useState('По инициативе собственника');
  const [techConditionCategory, setTechConditionCategory] = useState('Не установлено');
  const [preliminaryCategory, setPreliminaryCategory] = useState(false);
  const [hideTechCondition, setHideTechCondition] = useState(false);
  const [operationConditions, setOperationConditions] = useState('');
  const [checkedBy, setCheckedBy] = useState('');
  const [qualityControl, setQualityControl] = useState('');
  const [developedBy, setDevelopedBy] = useState('');
  const [firstSignature, setFirstSignature] = useState('Степанов Д.В., генеральный директор');
  const [secondSignature, setSecondSignature] = useState('Маёршин Р.В., главный инженер проекта');
  const [isSigned, setIsSigned] = useState(false);

  useEffect(() => {
    if (initialData) {
      setOrganization(initialData.organization || 'ООО "Союзстальконструкция"');
      setExaminationDate(initialData.examinationDate || '');
      setTakeDateFromPhotos(initialData.takeDateFromPhotos || false);
      setNextExaminationDate(initialData.nextExaminationDate || '');
      setReason(initialData.reason || 'По инициативе собственника');
      setTechConditionCategory(initialData.techConditionCategory || 'Не установлено');
      setPreliminaryCategory(initialData.preliminaryCategory || false);
      setHideTechCondition(initialData.hideTechCondition || false);
      setOperationConditions(initialData.operationConditions || '');
      setCheckedBy(initialData.checkedBy || '');
      setQualityControl(initialData.qualityControl || '');
      setDevelopedBy(initialData.developedBy || '');
      setFirstSignature(initialData.firstSignature || 'Степанов Д.В., генеральный директор');
      setSecondSignature(initialData.secondSignature || 'Маёршин Р.В., главный инженер проекта');
      setIsSigned(initialData.isSigned || false);
    }
  }, [initialData]);

  const handleOrganizationChange = (e) => setOrganization(e.target.value);
  const handleExaminationDateChange = (e) => setExaminationDate(e.target.value);
  const handleTakeDateFromPhotosChange = (e) => setTakeDateFromPhotos(e.target.checked);
  const handleNextExaminationDateChange = (e) => setNextExaminationDate(e.target.value);
  const handleReasonChange = (e) => setReason(e.target.value);
  const handleTechConditionCategoryChange = (e) => setTechConditionCategory(e.target.value);
  const handlePreliminaryCategoryChange = (e) => setPreliminaryCategory(e.target.checked);
  const handleHideTechConditionChange = (e) => setHideTechCondition(e.target.checked);
  const handleOperationConditionsChange = (e) => setOperationConditions(e.target.value);
  const handleCheckedByChange = (e) => setCheckedBy(e.target.value);
  const handleQualityControlChange = (e) => setQualityControl(e.target.value);
  const handleDevelopedByChange = (e) => setDevelopedBy(e.target.value);
  const handleFirstSignatureChange = (e) => setFirstSignature(e.target.value);
  const handleSecondSignatureChange = (e) => setSecondSignature(e.target.value);

  const handleAddOperationCondition = () => {
    // Логика для добавления условий эксплуатации
    console.log('Добавить условие эксплуатации');
  };

  const handleFirstSign = () => {
    console.log('Подписать первую подпись');
  };

  const handleSecondSign = () => {
    console.log('Подписать вторую подпись');
  };

  const handleSignDocument = () => {
    setIsSigned(true);
    console.log('Документ подписан');
  };

  const getFormDataForSave = () => {
    return {
      organization,
      examinationDate,
      takeDateFromPhotos,
      nextExaminationDate,
      reason,
      techConditionCategory,
      preliminaryCategory,
      hideTechCondition,
      operationConditions,
      checkedBy,
      qualityControl,
      developedBy,
      firstSignature,
      secondSignature,
      isSigned
    };
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (onSave) {
        const formData = getFormDataForSave();
        await onSave(formData);
      }
    },
    getFormData: getFormDataForSave
  }));

  return (
    <CContainer fluid className="organization-info-tab-container">
      <CForm>
        {/* Организация, проводившая обследование */}
        <CRow className="mb-3 align-items-center">
          <CCol md={3}>
            <CFormLabel htmlFor="organization">Организация, проводившая обследование:</CFormLabel>
          </CCol>
          <CCol md={9}>
            <CFormInput
              type="text"
              id="organization"
              value={organization}
              onChange={handleOrganizationChange}
            />
          </CCol>
        </CRow>

        {/* Время проведения обследования */}
        <CRow className="mb-3 align-items-center">
          <CCol md={3}>
            <CFormLabel htmlFor="examinationDate">Время проведения обследования:</CFormLabel>
          </CCol>
          <CCol md={2}>
            <CFormInput
              type="date"
              id="examinationDate"
              value={examinationDate}
              onChange={handleExaminationDateChange}
            />
          </CCol>
          <CCol md={3}>
            <CFormCheck
              id="takeDateFromPhotos"
              label="Взять дату из фотоматериалов"
              checked={takeDateFromPhotos}
              onChange={handleTakeDateFromPhotosChange}
            />
          </CCol>
          <CCol md={2}>
            <CFormLabel htmlFor="nextExaminationDate">Срок очередного обследования:</CFormLabel>
          </CCol>
          <CCol md={2}>
            <CFormInput
              type="date"
              id="nextExaminationDate"
              value={nextExaminationDate}
              onChange={handleNextExaminationDateChange}
            />
          </CCol>
        </CRow>

        {/* Причина обследования */}
        <CRow className="mb-3 align-items-start">
          <CCol md={3}>
            <CFormLabel htmlFor="reason">Причина обследования:</CFormLabel>
          </CCol>
          <CCol md={9}>
            <CFormTextarea
              id="reason"
              value={reason}
              onChange={handleReasonChange}
              rows={3}
            />
          </CCol>
        </CRow>

        {/* Категория технического состояния */}
        <CRow className="mb-3 align-items-center">
          <CCol md={3}>
            <CFormLabel>Категория технического состояния:</CFormLabel>
          </CCol>
          <CCol md={3}>
            <CButton 
              color="light" 
              variant="outline"
              size="sm" 
              className="text-dark border w-100"
            >
              Не установлено
            </CButton>
          </CCol>
          <CCol md={6}>
            <CFormSelect
              value={techConditionCategory}
              onChange={handleTechConditionCategoryChange}
            >
              <option value="Не установлено">Не установлено</option>
              {/* Здесь будут добавляться опции из API */}
            </CFormSelect>
          </CCol>
        </CRow>

        {/* Чекбоксы */}
        <CRow className="mb-3">
          <CCol md={3}></CCol>
          <CCol md={9}>
            <div className="d-flex gap-4">
              <CFormCheck
                id="preliminaryCategory"
                label="Предварительная категория"
                checked={preliminaryCategory}
                onChange={handlePreliminaryCategoryChange}
              />
              <CFormCheck
                id="hideTechCondition"
                label="Не выводить категорию технического состояния"
                checked={hideTechCondition}
                onChange={handleHideTechConditionChange}
              />
            </div>
          </CCol>
        </CRow>

        {/* Условия дальнейшей эксплуатации */}
        <CRow className="mb-3 align-items-start">
          <CCol md={3}>
            <CFormLabel>Условия дальнейшей эксплуатации:</CFormLabel>
          </CCol>
          <CCol md={9}>
            <div className="d-flex gap-2 mb-2">
              <CButton 
                color="light" 
                variant="outline" 
                size="sm" 
                className="text-dark border"
                onClick={handleAddOperationCondition}
              >
                +
              </CButton>
            </div>
            <CFormTextarea
              value={operationConditions}
              onChange={handleOperationConditionsChange}
              rows={3}
              placeholder="Введите условия дальнейшей эксплуатации..."
            />
          </CCol>
        </CRow>

        {/* Разделительная линия */}
        <CRow className="mb-3">
          <CCol>
            <div className="border-bottom mt-2 mb-3"></div>
          </CCol>
        </CRow>

        {/* Исполнители */}
        <CRow className="mb-3">
          <CCol>
            <CFormLabel className="fw-bold">Исполнители:</CFormLabel>
          </CCol>
        </CRow>

        {/* Проверил, Н. контроль, Разработал */}
        <CRow className="mb-3 align-items-center">
          <CCol md={4}>
            <CFormLabel htmlFor="checkedBy">Проверил:</CFormLabel>
            <CFormInput
              type="text"
              id="checkedBy"
              value={checkedBy}
              onChange={handleCheckedByChange}
            />
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="qualityControl">Н. контроль:</CFormLabel>
            <CFormInput
              type="text"
              id="qualityControl"
              value={qualityControl}
              onChange={handleQualityControlChange}
            />
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="developedBy">Разработал:</CFormLabel>
            <CFormInput
              type="text"
              id="developedBy"
              value={developedBy}
              onChange={handleDevelopedByChange}
            />
          </CCol>
        </CRow>

        {/* Разделительная линия */}
        <CRow className="mb-3">
          <CCol>
            <div className="border-bottom mt-2 mb-3"></div>
          </CCol>
        </CRow>

        {/* Первая подпись */}
        <CRow className="mb-3 align-items-center">
          <CCol md={3}>
            <CFormLabel htmlFor="firstSignature">Первая подпись:</CFormLabel>
          </CCol>
          <CCol md={7}>
            <CFormInput
              type="text"
              id="firstSignature"
              value={firstSignature}
              onChange={handleFirstSignatureChange}
            />
          </CCol>
          <CCol md={2}>
            <CButton 
              color="light" 
              variant="outline"
              size="sm" 
              className="text-dark border w-100"
              onClick={handleFirstSign}
            >
              Подписать
            </CButton>
          </CCol>
        </CRow>

        {/* Вторая подпись */}
        <CRow className="mb-3 align-items-center">
          <CCol md={3}>
            <CFormLabel htmlFor="secondSignature">Вторая подпись:</CFormLabel>
          </CCol>
          <CCol md={7}>
            <CFormInput
              type="text"
              id="secondSignature"
              value={secondSignature}
              onChange={handleSecondSignatureChange}
            />
          </CCol>
          <CCol md={2}>
            <CButton 
              color="light" 
              variant="outline"
              size="sm" 
              className="text-dark border w-100"
              onClick={handleSecondSign}
            >
              Подписать
            </CButton>
          </CCol>
        </CRow>

        {/* Кнопка подписания документа */}
        <CRow className="mb-3">
          <CCol className="text-center">
            <CButton 
              color={isSigned ? "success" : "danger"}
              variant="outline"
              size="lg"
              className={isSigned ? "text-success border-success" : "text-danger border-danger"}
              onClick={handleSignDocument}
            >
              {isSigned ? 'ПОДПИСАНО' : 'НЕ ПОДПИСАНО'}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </CContainer>
  );
});

OrganizationInfoTab.propTypes = {
  initialData: PropTypes.shape({
    organization: PropTypes.string,
    examinationDate: PropTypes.string,
    takeDateFromPhotos: PropTypes.bool,
    nextExaminationDate: PropTypes.string,
    reason: PropTypes.string,
    techConditionCategory: PropTypes.string,
    preliminaryCategory: PropTypes.bool,
    hideTechCondition: PropTypes.bool,
    operationConditions: PropTypes.string,
    checkedBy: PropTypes.string,
    qualityControl: PropTypes.string,
    developedBy: PropTypes.string,
    firstSignature: PropTypes.string,
    secondSignature: PropTypes.string,
    isSigned: PropTypes.bool
  }),
  onSave: PropTypes.func
};

OrganizationInfoTab.defaultProps = {
  initialData: null,
  onSave: null
};

export default OrganizationInfoTab;